import { useCallback, useContext, useEffect, useState } from "react";
import { BarChart3, Settings } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button, EmptyState } from "../ui";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import ConversationPane from "./ConversationPane";
import DetailsPane from "./DetailsPane";
import AnalyticsPanel from "./AnalyticsPanel";
import InboxList from "./InboxList";
import StatsBar from "./StatsBar";
import { AssignDialog, ResolveDialog } from "./parts";
import CopilotSettings from "./settings/CopilotSettings";
import { SETTINGS_TEXT } from "./settings/copilotSettingsText";
import { useCopilotInbox } from "./useCopilotInbox";
import "./CopilotAdmin.css";

// Admin support inbox (route /auth/copilot, permission copilot.view).
//   desktop  list | conversation | details
//   tablet   list | conversation, details as a drawer
//   mobile   one view at a time: list, conversation, details
// The selected conversation lives in the URL (?c=<id>), so refresh and shared
// links keep it. The backend decides what each user may open: a conversation
// outside the user's scope answers 404 and the page says so.
export default function CopilotAdmin() {
  const { lang, currentUserInfo } = useContext(Context);
  const { can } = useAccess();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("c") || null;
  const [pane, setPane] = useState(selectedId ? "chat" : "list");
  const [dialog, setDialog] = useState(null); // "resolve" | "assign"
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");

  const access = {
    reply: can("copilot.reply"),
    changeStatus: can("copilot.changeStatus"),
    assign: can("copilot.assign"),
    stats: can("copilot.stats"),
    manageReplies: can("copilot.manageReplies"),
    manageSettings: can("copilot.manageSettings"),
    manageFaqs: can("copilot.manageFaqs"),
  };
  const inSettings = params.get("view") === "settings";
  // The inbox stops polling while the settings screen is open.
  const inbox = useCopilotInbox({ lang, selectedId, canStats: access.stats, canAssign: access.assign, visible: visible && !inSettings });
  const { t } = inbox;
  const rtl = lang === "ar";

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const select = useCallback((id) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("c", id);
      else next.delete("c");
      return next;
    });
    setPane(id ? "chat" : "list");
  }, [setParams]);

  const backToList = () => {
    setPane("list");
    // Keep the selection on wide screens; on small ones the list view is the "back" target.
    if (window.matchMedia?.("(max-width: 767px)").matches) select(null);
  };

  const openSettings = (tab) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("view", "settings");
    next.set("tab", tab || "general");
    return next;
  });
  const closeSettings = () => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete("view");
    next.delete("tab");
    return next;
  });

  const askAssign = () => {
    if (inbox.agents.status === "idle" || inbox.agents.status === "error") inbox.loadAgents();
    setDialog("assign");
  };

  if (inSettings) {
    return (
      <div className={`copilot-admin ${rtl ? "rtl" : ""}`} dir={rtl ? "rtl" : "ltr"}>
        <CopilotSettings lang={lang} tab={params.get("tab")} onTab={openSettings} onBack={closeSettings} access={access} agentName={currentUserInfo?.name || ""} />
      </div>
    );
  }

  return (
    <div className={`copilot-admin ${rtl ? "rtl" : ""}`} dir={rtl ? "rtl" : "ltr"} data-pane={pane}>
      <header className="ca-header">
        <div>
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
        <Button variant="neutral" appearance="outline" className="ca-toggle" icon={<Settings size={16} aria-hidden="true" />} onClick={() => openSettings("general")}>
          {SETTINGS_TEXT[lang]?.title || SETTINGS_TEXT.en.title}
        </Button>
        {access.stats && (
          <Button variant={showAnalytics ? "primary" : "neutral"} appearance={showAnalytics ? "soft" : "outline"} className="ca-toggle" aria-expanded={showAnalytics} aria-controls="ca-analytics" icon={<BarChart3 size={16} aria-hidden="true" />} onClick={() => setShowAnalytics((v) => !v)}>
            {t.analytics.button}
          </Button>
        )}
      </header>
      {access.stats && <StatsBar inbox={inbox} />}
      {access.stats && showAnalytics && <AnalyticsPanel inbox={inbox} lang={lang} />}
      <div className="ca-workspace" data-has-selection={Boolean(selectedId)}>
        <section className="ca-pane ca-pane-list" aria-label={t.viewList}>
          <InboxList inbox={inbox} lang={lang} selectedId={selectedId} onSelect={select} canAssign={access.assign} />
        </section>
        <section className="ca-pane ca-pane-chat" aria-label={t.viewChat}>
          {selectedId ? (
            <ConversationPane
              inbox={inbox}
              lang={lang}
              selectedId={selectedId}
              access={access}
              agentName={currentUserInfo?.name || ""}
              onBack={backToList}
              onDetails={() => setPane("details")}
              onAskResolve={() => setDialog("resolve")}
              onAskAssign={askAssign}
            />
          ) : (
            <EmptyState className="ca-fill" title={t.selectOne}>{t.selectOneBody}</EmptyState>
          )}
        </section>
        <aside className="ca-pane ca-pane-details" aria-label={t.details}>
          {selectedId ? (
            <DetailsPane inbox={inbox} lang={lang} access={access} onClose={() => setPane("chat")} onAskResolve={() => setDialog("resolve")} onAskAssign={askAssign} />
          ) : (
            <EmptyState className="ca-fill">{t.selectOneBody}</EmptyState>
          )}
        </aside>
      </div>
      {pane === "details" && <button type="button" className="ca-scrim" aria-label={t.closeDetails} onClick={() => setPane("chat")} />}

      {dialog === "resolve" && (
        <ResolveDialog
          t={t}
          lang={lang}
          error={inbox.notice?.type === "error" ? inbox.notice.text : null}
          onCancel={() => setDialog(null)}
          onConfirm={async (note) => {
            if (await inbox.changeStatus("resolved", note)) setDialog(null);
          }}
        />
      )}
      {inbox.confirmationDialog}
      {dialog === "assign" && (
        <AssignDialog
          t={t}
          lang={lang}
          error={inbox.notice?.type === "error" ? inbox.notice.text : null}
          agents={inbox.agents}
          current={typeof inbox.detail.conversation?.assignedTo === "object" ? inbox.detail.conversation?.assignedTo?.id : ""}
          onCancel={() => setDialog(null)}
          onConfirm={async (assigneeId) => {
            if (await inbox.assign(assigneeId)) setDialog(null);
          }}
        />
      )}
    </div>
  );
}
